import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { DocumentAttachment } from 'models';
import { NGXLogger } from 'ngx-logger';
import { Observable, defer, switchMap } from 'rxjs';
import { EmailData } from '../common/email-data';

/** Email attachments service. */
@Injectable({
  providedIn: 'root',
})
export class EmailAttachmentsService {
  private httpClient = inject(HttpClient);
  private logger = inject(NGXLogger);
  private translateService = inject(TranslocoService);

  /**
   * Retrieves email attachments from an email file.
   *
   * @param type Type of email.
   * @param url URL to the email file.
   * @returns An observable array of email attachments.
   */
  getAttachmentsFromEmail(
    type: 'eml' | 'msg',
    url: string
  ): Observable<DocumentAttachment[]> {
    if (type === 'eml') {
      return this.getAttachmentsFromEml(url);
    } else {
      return this.getAttachmentsFromMsg(url);
    }
  }

  /**
   * Retrieves email attachments from EML files.
   *
   * @param url Url to the eml file.
   * @returns An observable array of email attachments.
   */
  getAttachmentsFromEml(url: string) {
    return this.getBytesFromUrl(url).pipe(
      switchMap((arrayBuffer) => {
        return defer(async () => {
          const emailData = await EmailData.fromEml(arrayBuffer);
          return emailData.attachments;
        });
      })
    );
  }
  /**
   * Retrieves email attachments from MSG files.
   *
   * @param url Url to the msg file.
   * @returns An observable array of email attachments.
   */
  getAttachmentsFromMsg(url: string) {
    return this.getBytesFromUrl(url).pipe(
      switchMap((arrayBuffer) => {
        return defer(async () => {
          const emailData = await EmailData.fromMsg(
            arrayBuffer,
            this.translateService,
            this.logger
          );

          return emailData.attachments;
        });
      })
    );
  }

  private getBytesFromUrl(url: string) {
    return this.httpClient.get(url, {
      responseType: 'arraybuffer',
    });
  }
}
